import { Category } from '../types/Category';
import { Content } from '../types/Content';
import { normalizeCategoryName } from './CategoryUtils';
import {
  CompareNodeStatus,
  ContentCompareBuckets,
} from '../types/DocumentCompare';

const trimCategoryId = (id: string) => id.replace(/^\uFEFF/g, '').trim();

export function normalizeCompareText(text: string): string {
  return (text || '')
    .replace(/\u00A0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function contentCompareKey(subcase: string, text: string): string {
  return `${(subcase || '').trim()}\u0000${normalizeCompareText(text)}`;
}

export function buildCategoryPathMap(
  root: Category,
  parentPath: string[] = [],
): Map<string, string[]> {
  const map = new Map<string, string[]>();
  const path = [...parentPath, root.name];
  map.set(root.id, path);
  for (const child of root.children ?? []) {
    const childMap = buildCategoryPathMap(child, path);
    childMap.forEach((value, key) => map.set(key, value));
  }
  return map;
}

export function findCategoryByPath(root: Category, path: string[]): Category | null {
  let current: Category | null = root;
  for (const segment of path) {
    if (!current) {
      return null;
    }
    const segNorm = segment.trim().toLowerCase();
    const children: Category[] = current.children ?? [];
    const next: Category | undefined = children.find(
      (c: Category) => c.name.trim().toLowerCase() === segNorm,
    );
    current = next ?? null;
  }
  return current;
}

/** API may expose branch vs content flag as `contentCategory` or legacy `isContentCategory`. */
export function categoryIsContentCategory(
  category: Category | undefined | null,
): boolean {
  if (!category) {
    return false;
  }
  if (category.contentCategory === true) {
    return true;
  }
  const legacy = (category as Category & { isContentCategory?: boolean }).isContentCategory;
  return legacy === true;
}

export function categoryIsBranchCategory(
  category: Category | undefined | null,
): boolean {
  return category != null && !categoryIsContentCategory(category);
}

/** True when `selectedPath` sits under a branch that exists on only one document side. */
export function hasOneSidedBranchAncestor(
  leftRoot: Category,
  rightRoot: Category,
  selectedPath: string[],
): boolean {
  for (let i = 1; i < selectedPath.length; i++) {
    const ancestorPath = selectedPath.slice(0, i);
    const leftA = findCategoryByPath(leftRoot, ancestorPath);
    const rightA = findCategoryByPath(rightRoot, ancestorPath);
    if ((leftA != null) !== (rightA != null)) {
      const sole = leftA ?? rightA;
      if (sole && !sole.contentCategory) {
        return true;
      }
    }
  }
  return false;
}

function categoriesHaveIdenticalContent(left: Category, right: Category): boolean {
  if (left.contentCategory && right.contentCategory) {
    const { leftOnly, rightOnly } = compareContentRows(left.contents, right.contents);
    return leftOnly.length === 0 && rightOnly.length === 0;
  }

  const rows = buildComparedSubtreeContentRows(
    collectSubtreeContentEntries(left),
    collectSubtreeContentEntries(right),
    () => undefined,
    () => undefined,
  );
  return rows.every((row) => row.status === 'both');
}

/** Whether this side lacks content that exists on the other (subcase + text). */
function sideIsMissingContentComparedToOther(
  left: Category,
  right: Category,
  side: 'left' | 'right',
): boolean {
  if (left.contentCategory && right.contentCategory) {
    const { leftOnly, rightOnly } = compareContentRows(left.contents, right.contents);
    return side === 'left' ? rightOnly.length > 0 : leftOnly.length > 0;
  }

  const rows = buildComparedSubtreeContentRows(
    collectSubtreeContentEntries(left),
    collectSubtreeContentEntries(right),
    () => undefined,
    () => undefined,
  );
  return side === 'left'
    ? rows.some((row) => row.status === 'right-only')
    : rows.some((row) => row.status === 'left-only');
}

/** True when both document trees align (structure, types, and content) for compare. */
export function compareDocumentsFullyMatch(
  leftRoot: Category,
  rightRoot: Category,
): boolean {
  const rootStatus = getNodeCompareStatus(leftRoot, rightRoot);
  if (
    rootStatus === 'left-only' ||
    rootStatus === 'right-only' ||
    rootStatus === 'type-conflict' ||
    rootStatus === 'missing-content'
  ) {
    return false;
  }
  if (leftRoot.contentCategory && rightRoot.contentCategory) {
    return true;
  }
  return !branchSubtreeHasCompareDifferences(leftRoot, rightRoot, []);
}

/** True if any category under this branch pair differs between documents (not fully Match). */
export function branchSubtreeHasCompareDifferences(
  leftRoot: Category,
  rightRoot: Category,
  branchRelPath: string[],
): boolean {
  const visitChildren = (parentRelPath: string[]): boolean => {
    for (const { relPath } of listCompareTreeChildren(leftRoot, rightRoot, parentRelPath)) {
      const leftNode = findCategoryByPath(leftRoot, relPath) ?? undefined;
      const rightNode = findCategoryByPath(rightRoot, relPath) ?? undefined;
      const status = getNodeCompareStatus(leftNode, rightNode, {
        parentTypeConflict: pathHasTypeConflict(leftRoot, rightRoot, parentRelPath),
      });
      if (status !== 'match') {
        return true;
      }
      const node = leftNode ?? rightNode;
      if (node && !node.contentCategory && visitChildren(relPath)) {
        return true;
      }
    }
    return false;
  };
  return visitChildren(branchRelPath);
}

export function pathHasTypeConflict(
  leftRoot: Category,
  rightRoot: Category,
  relPath: string[],
): boolean {
  const left =
    relPath.length === 0 ? leftRoot : findCategoryByPath(leftRoot, relPath);
  const right =
    relPath.length === 0 ? rightRoot : findCategoryByPath(rightRoot, relPath);
  return !!(
    left &&
    right &&
    left.contentCategory !== right.contentCategory
  );
}

/**
 * Whether a compare tree row at {@link relPath} should appear selected on {@link side}.
 * Paired paths highlight on both trees when the category exists on each side and types align.
 * Type conflicts highlight only on {@link selectedTreeSide} so each side can be inspected separately.
 */
export function comparePathSelectedOnSide(
  side: 'left' | 'right',
  relPath: string[],
  selectedPath: string[] | null,
  selectedTreeSide: 'left' | 'right',
  leftRoot: Category,
  rightRoot: Category,
): boolean {
  if (selectedPath == null) {
    return false;
  }
  if (selectedPath.length !== relPath.length) {
    return false;
  }
  if (!selectedPath.every((segment, index) => segment === relPath[index])) {
    return false;
  }
  if (pathHasTypeConflict(leftRoot, rightRoot, relPath)) {
    return selectedTreeSide === side;
  }
  const onSide =
    relPath.length === 0
      ? side === 'left'
        ? leftRoot
        : rightRoot
      : findCategoryByPath(side === 'left' ? leftRoot : rightRoot, relPath);
  return onSide != null;
}

/** MUI TreeView `selected` node id for the current compare selection on one document side. */
export function compareTreeSelectedNodeId(
  side: 'left' | 'right',
  root: Category,
  selectedPath: string[] | null,
  selectedTreeSide: 'left' | 'right',
  leftRoot: Category,
  rightRoot: Category,
): string {
  if (selectedPath == null) {
    return '';
  }
  if (
    !comparePathSelectedOnSide(
      side,
      selectedPath,
      selectedPath,
      selectedTreeSide,
      leftRoot,
      rightRoot,
    )
  ) {
    return '';
  }
  if (selectedPath.length === 0) {
    return compareTreeNodeId(side, root.id);
  }
  const cat = findCategoryByPath(root, selectedPath);
  return cat ? compareTreeNodeId(side, cat.id) : '';
}

export interface CompareNodeStatusOptions {
  parentTypeConflict?: boolean;
  /** When set, branch nodes may report {@link CompareNodeStatus.has-child-diff}. */
  treeContext?: {
    leftRoot: Category;
    rightRoot: Category;
    relPath: string[];
  };
}

/** True when a paired branch has at least one direct child whose pair status is not Match. */
export function branchDirectChildrenIncludeNonMatch(
  leftRoot: Category,
  rightRoot: Category,
  branchRelPath: string[],
): boolean {
  const parentTypeConflict = pathHasTypeConflict(leftRoot, rightRoot, branchRelPath);
  for (const { relPath } of listCompareTreeChildren(leftRoot, rightRoot, branchRelPath)) {
    const leftChild = findCategoryByPath(leftRoot, relPath) ?? undefined;
    const rightChild = findCategoryByPath(rightRoot, relPath) ?? undefined;
    if (
      getNodeCompareStatus(leftChild, rightChild, { parentTypeConflict }) !== 'match'
    ) {
      return true;
    }
  }
  return false;
}

export function getNodeCompareStatus(
  left: Category | undefined,
  right: Category | undefined,
  options?: CompareNodeStatusOptions,
): CompareNodeStatus {
  if (left && right) {
    if (left.contentCategory !== right.contentCategory) {
      return 'type-conflict';
    }
    if (!left.contentCategory && !right.contentCategory) {
      const ctx = options?.treeContext;
      if (
        ctx &&
        branchDirectChildrenIncludeNonMatch(
          ctx.leftRoot,
          ctx.rightRoot,
          ctx.relPath,
        )
      ) {
        return 'has-child-diff';
      }
      return 'match';
    }
    if (categoriesHaveIdenticalContent(left, right)) {
      return 'match';
    }
    return 'missing-content';
  }
  if (options?.parentTypeConflict) {
    return 'unpaired';
  }
  if (left) {
    return 'left-only';
  }
  return 'right-only';
}

/** Per-tree status: the side that already has all of the other's content shows Match. */
export function getNodeCompareStatusForSide(
  left: Category | undefined,
  right: Category | undefined,
  side: 'left' | 'right',
  options?: CompareNodeStatusOptions,
): CompareNodeStatus {
  const pairStatus = getNodeCompareStatus(left, right, options);
  if (pairStatus !== 'missing-content' || !left || !right) {
    return pairStatus;
  }
  return sideIsMissingContentComparedToOther(left, right, side)
    ? 'missing-content'
    : 'match';
}

export interface ComparedCategoryRow {
  status: CompareNodeStatus;
  key: string;
  displayName: string;
  childRelPath: string[];
  leftCategory?: Category;
  rightCategory?: Category;
}

/** Content or subtree rows for one document side (type-conflict tree selection). */
export function buildSingleSideContentRows(
  category: Category,
  side: 'left' | 'right',
): ComparedContentRow[] {
  if (category.contentCategory) {
    return (category.contents ?? []).map((content) => ({
      status: side === 'left' ? 'left-only' : 'right-only',
      key: `${side}:${content.id}`,
      content,
    }));
  }
  const entries = collectSubtreeContentEntries(category);
  return buildComparedSubtreeContentRows(
    side === 'left' ? entries : [],
    side === 'right' ? entries : [],
    () => undefined,
    () => undefined,
  );
}

/** Direct children of a branch on one document side (type-conflict tree selection). */
export function buildSingleSideChildCategoryRows(
  parentRelPath: string[],
  category: Category,
  side: 'left' | 'right',
): ComparedCategoryRow[] {
  const status: CompareNodeStatus = 'unpaired';
  return (category.children ?? []).map((child) => {
    const childRelPath = [...parentRelPath, child.name];
    return {
      status,
      key: childRelPath.join('\u0000'),
      displayName: child.name,
      childRelPath,
      leftCategory: side === 'left' ? child : undefined,
      rightCategory: side === 'right' ? child : undefined,
    };
  });
}

/** Direct child categories under the selected branch (same union rules as compare trees). */
export function buildComparedChildCategoryRows(
  leftRoot: Category,
  rightRoot: Category,
  parentRelPath: string[],
  viewSide: 'left' | 'right' = 'left',
): ComparedCategoryRow[] {
  const parentTypeConflict = pathHasTypeConflict(leftRoot, rightRoot, parentRelPath);
  return listCompareTreeChildren(leftRoot, rightRoot, parentRelPath).map(
    ({ displayName, relPath }) => {
      const leftChild = findCategoryByPath(leftRoot, relPath) ?? undefined;
      const rightChild = findCategoryByPath(rightRoot, relPath) ?? undefined;
      return {
        status: getNodeCompareStatusForSide(leftChild, rightChild, viewSide, {
          parentTypeConflict,
        }),
        key: relPath.join('\u0000'),
        displayName,
        childRelPath: relPath,
        leftCategory: leftChild,
        rightCategory: rightChild,
      };
    },
  );
}

export function compareContentRows(
  leftContents: Content[] | undefined,
  rightContents: Content[] | undefined,
): ContentCompareBuckets {
  const left = leftContents ?? [];
  const right = rightContents ?? [];
  const rightByKey = new Map<string, Content>();
  for (const row of right) {
    rightByKey.set(contentCompareKey(row.subcase, row.text), row);
  }

  const both: Content[] = [];
  const leftOnly: Content[] = [];
  const matchedRightKeys = new Set<string>();

  for (const row of left) {
    const key = contentCompareKey(row.subcase, row.text);
    if (rightByKey.has(key)) {
      both.push(row);
      matchedRightKeys.add(key);
    } else {
      leftOnly.push(row);
    }
  }

  const rightOnly: Content[] = [];
  for (const row of right) {
    const key = contentCompareKey(row.subcase, row.text);
    if (!matchedRightKeys.has(key)) {
      rightOnly.push(row);
    }
  }

  return { both, leftOnly, rightOnly };
}

export type ComparedContentRowStatus = 'both' | 'left-only' | 'right-only';

export interface ComparedContentRow {
  status: ComparedContentRowStatus;
  key: string;
  content: Content;
  /** Path from selected branch down to the content category (names). Empty when the selection is a content category. */
  categoryRelPath?: string[];
  leftContentCategoryId?: string;
  rightContentCategoryId?: string;
}

export interface SubtreeContentEntry {
  content: Content;
  contentCategoryId: string;
  categoryRelPath: string[];
}

/** All content rows under a branch or content category (includes nested content categories). */
export function collectSubtreeContentEntries(branchOrContentCategory: Category): SubtreeContentEntry[] {
  const out: SubtreeContentEntry[] = [];
  const walk = (cat: Category, relPath: string[]) => {
    if (cat.contentCategory) {
      for (const content of cat.contents ?? []) {
        out.push({
          content,
          contentCategoryId: cat.id,
          categoryRelPath: relPath,
        });
      }
      return;
    }
    for (const child of cat.children ?? []) {
      walk(child, [...relPath, child.name]);
    }
  };
  walk(branchOrContentCategory, []);
  return out;
}

export function findContentCategoryAtRelPath(
  branchRoot: Category,
  categoryRelPath: string[],
): Category | undefined {
  const cat =
    categoryRelPath.length === 0 ? branchRoot : findCategoryByPath(branchRoot, categoryRelPath);
  return cat?.contentCategory ? cat : undefined;
}

function subtreeContentScopeKey(
  categoryRelPath: string[],
  subcase: string,
  text: string,
): string {
  return `${categoryRelPath.join('/')}\u0000${contentCompareKey(subcase, text)}`;
}

/** Compare content from two subtrees (branch or content category), matched by path + subcase + text. */
export function buildComparedSubtreeContentRows(
  leftEntries: SubtreeContentEntry[],
  rightEntries: SubtreeContentEntry[],
  resolveLeftTarget: (categoryRelPath: string[]) => string | undefined,
  resolveRightTarget: (categoryRelPath: string[]) => string | undefined,
): ComparedContentRow[] {
  const rightByKey = new Map<string, SubtreeContentEntry>();
  for (const entry of rightEntries) {
    rightByKey.set(
      subtreeContentScopeKey(entry.categoryRelPath, entry.content.subcase, entry.content.text),
      entry,
    );
  }

  const rows: ComparedContentRow[] = [];
  const matchedKeys = new Set<string>();

  for (const left of leftEntries) {
    const key = subtreeContentScopeKey(
      left.categoryRelPath,
      left.content.subcase,
      left.content.text,
    );
    const right = rightByKey.get(key);
    const status: ComparedContentRowStatus = right ? 'both' : 'left-only';
    if (right) {
      matchedKeys.add(key);
    }
    rows.push({
      status,
      key,
      content: left.content,
      categoryRelPath: left.categoryRelPath,
      leftContentCategoryId: left.contentCategoryId,
      rightContentCategoryId:
        right?.contentCategoryId ?? resolveRightTarget(left.categoryRelPath),
    });
  }

  for (const right of rightEntries) {
    const key = subtreeContentScopeKey(
      right.categoryRelPath,
      right.content.subcase,
      right.content.text,
    );
    if (matchedKeys.has(key)) {
      continue;
    }
    rows.push({
      status: 'right-only',
      key,
      content: right.content,
      categoryRelPath: right.categoryRelPath,
      leftContentCategoryId: resolveLeftTarget(right.categoryRelPath),
      rightContentCategoryId: right.contentCategoryId,
    });
  }

  return rows;
}

/** One row per content line (left order, then right-only), with merge-compare status. */
export function buildComparedContentRows(
  leftContents: Content[] | undefined,
  rightContents: Content[] | undefined,
): ComparedContentRow[] {
  const left = (leftContents ?? []).map((content) => ({
    content,
    contentCategoryId: '',
    categoryRelPath: [] as string[],
  }));
  const right = (rightContents ?? []).map((content) => ({
    content,
    contentCategoryId: '',
    categoryRelPath: [] as string[],
  }));
  return buildComparedSubtreeContentRows(
    left,
    right,
    () => undefined,
    () => undefined,
  );
}

export function comparedRowSelectionKey(row: ComparedContentRow): string {
  return `${row.status}:${row.content.id}`;
}

export function comparedCategorySelectionKey(row: ComparedCategoryRow): string {
  const id = row.rightCategory?.id ?? row.leftCategory?.id ?? row.key;
  return `cat:${row.status}:${id}`;
}

export interface TreeCategoryCopyTarget {
  targetParentCategoryId: string;
  sourceCategoryId: string;
  rowKey: string;
}

/** Copy a right-only category selected in the tree into the left document. */
export function buildTreeCategoryCopyToLeft(
  leftRoot: Category,
  selectedPath: string[],
  selectedRight: Category,
): TreeCategoryCopyTarget | null {
  if (selectedPath.length === 0) {
    return null;
  }
  const parentPath = selectedPath.slice(0, -1);
  const targetParent =
    parentPath.length === 0 ? leftRoot : findCategoryByPath(leftRoot, parentPath);
  if (!targetParent || targetParent.contentCategory) {
    return null;
  }
  return {
    targetParentCategoryId: targetParent.id,
    sourceCategoryId: selectedRight.id,
    rowKey: relPathToKey(selectedPath),
  };
}

/** Copy a left-only category selected in the tree into the right document. */
export function buildTreeCategoryCopyToRight(
  rightRoot: Category,
  selectedPath: string[],
  selectedLeft: Category,
): TreeCategoryCopyTarget | null {
  if (selectedPath.length === 0) {
    return null;
  }
  const parentPath = selectedPath.slice(0, -1);
  const targetParent =
    parentPath.length === 0 ? rightRoot : findCategoryByPath(rightRoot, parentPath);
  if (!targetParent || targetParent.contentCategory) {
    return null;
  }
  return {
    targetParentCategoryId: targetParent.id,
    sourceCategoryId: selectedLeft.id,
    rowKey: relPathToKey(selectedPath),
  };
}

/** Category rows that can be copied or type-merged across documents in merge compare. */
export function comparedCategoryCanSelectForMerge(
  row: ComparedCategoryRow,
  rightIsReadOnlyShared: boolean,
): boolean {
  if (row.status === 'type-conflict') {
    return !!(row.leftCategory && row.rightCategory);
  }
  if (row.status === 'right-only') {
    return !!row.rightCategory;
  }
  if (row.status === 'left-only' && !rightIsReadOnlyShared) {
    return !!row.leftCategory;
  }
  return false;
}

/** Rows that can be copied across documents in merge compare. */
export function comparedRowCanSelectForMerge(
  row: ComparedContentRow,
  rightIsReadOnlyShared: boolean,
): boolean {
  if (row.status === 'right-only') {
    return true;
  }
  if (row.status === 'left-only' && !rightIsReadOnlyShared) {
    return true;
  }
  return false;
}

export function collectCategoryIds(root: Category): string[] {
  const ids: string[] = [];
  const walk = (cat: Category) => {
    ids.push(cat.id);
    for (const child of cat.children ?? []) {
      walk(child);
    }
  };
  walk(root);
  return ids;
}

export function compareTreeNodeId(side: 'left' | 'right', categoryId: string): string {
  return `${side}-${categoryId}`;
}

export function collectCompareTreeNodeIds(root: Category, side: 'left' | 'right'): string[] {
  return collectCategoryIds(root).map((id) => compareTreeNodeId(side, id));
}

export interface CompareTreeChildRef {
  displayName: string;
  relPath: string[];
}

/** Union of child names at `path` (case-insensitive key), without top-level duplicate-root handling. */
function listRawChildNamesAtPath(
  leftRoot: Category,
  rightRoot: Category,
  path: string[],
): string[] {
  const left = path.length === 0 ? leftRoot : findCategoryByPath(leftRoot, path);
  const right = path.length === 0 ? rightRoot : findCategoryByPath(rightRoot, path);
  const names = new Map<string, string>();
  for (const c of left?.children ?? []) {
    const key = c.name.trim().toLowerCase();
    if (!names.has(key)) {
      names.set(key, c.name);
    }
  }
  for (const c of right?.children ?? []) {
    const key = c.name.trim().toLowerCase();
    if (!names.has(key)) {
      names.set(key, c.name);
    }
  }
  let result = Array.from(names.values());
  if (path.length === 0) {
    const rootNames = new Set([
      leftRoot.name.trim().toLowerCase(),
      rightRoot.name.trim().toLowerCase(),
    ]);
    result = result.filter((name) => !rootNames.has(name.trim().toLowerCase()));
  }
  return result;
}

/**
 * Children to render under `parentRelPath`. At the document root, skips a duplicate
 * same-named leaf beside the root row; hoists grandchildren when that wrapper holds the tree.
 */
export function listCompareTreeChildren(
  leftRoot: Category,
  rightRoot: Category,
  parentRelPath: string[],
): CompareTreeChildRef[] {
  const rawNames = listRawChildNamesAtPath(leftRoot, rightRoot, parentRelPath);
  const out: CompareTreeChildRef[] = [];
  const seen = new Set<string>();

  const push = (displayName: string, relPath: string[]) => {
    const key = relPath.join('\u0000');
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    out.push({ displayName, relPath });
  };

  if (parentRelPath.length === 0) {
    const skipNameKeys = new Set([
      normalizeCategoryName(leftRoot.name),
      normalizeCategoryName(rightRoot.name),
    ]);
    for (const name of rawNames) {
      const nameKey = normalizeCategoryName(name);
      const relPath = [name];
      const leftChild = findChildAtPath(leftRoot, [], name);
      const rightChild = findChildAtPath(rightRoot, [], name);
      if (leftChild && trimCategoryId(leftChild.id) === trimCategoryId(leftRoot.id)) {
        continue;
      }
      if (rightChild && trimCategoryId(rightChild.id) === trimCategoryId(rightRoot.id)) {
        continue;
      }
      if (skipNameKeys.has(nameKey)) {
        const hoisted = listCompareTreeChildren(leftRoot, rightRoot, relPath);
        if (hoisted.length > 0) {
          for (const child of hoisted) {
            push(child.displayName, child.relPath);
          }
          continue;
        }
        continue;
      }
      push(name, relPath);
    }
    return out;
  }

  for (const name of rawNames) {
    push(name, [...parentRelPath, name]);
  }
  return out;
}

/** Child names under `path` on a single document tree (no cross-document union). */
function listRawChildNamesForRoot(root: Category, path: string[]): string[] {
  const parent = path.length === 0 ? root : findCategoryByPath(root, path);
  const names = new Map<string, string>();
  for (const c of parent?.children ?? []) {
    const key = c.name.trim().toLowerCase();
    if (!names.has(key)) {
      names.set(key, c.name);
    }
  }
  let result = Array.from(names.values());
  if (path.length === 0) {
    const rootKey = root.name.trim().toLowerCase();
    result = result.filter((name) => name.trim().toLowerCase() !== rootKey);
  }
  return result;
}

/**
 * Children to render under `parentRelPath` for one side only (that document's categories).
 */
export function listSideCompareTreeChildren(
  root: Category,
  parentRelPath: string[],
): CompareTreeChildRef[] {
  const rawNames = listRawChildNamesForRoot(root, parentRelPath);
  const out: CompareTreeChildRef[] = [];
  const seen = new Set<string>();

  const push = (displayName: string, relPath: string[]) => {
    const key = relPath.join('\u0000');
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    out.push({ displayName, relPath });
  };

  if (parentRelPath.length === 0) {
    const skipNameKeys = new Set([normalizeCategoryName(root.name)]);
    for (const name of rawNames) {
      const nameKey = normalizeCategoryName(name);
      const relPath = [name];
      const child = findChildAtPath(root, [], name);
      if (child && trimCategoryId(child.id) === trimCategoryId(root.id)) {
        continue;
      }
      if (skipNameKeys.has(nameKey)) {
        const hoisted = listSideCompareTreeChildren(root, relPath);
        if (hoisted.length > 0) {
          for (const h of hoisted) {
            push(h.displayName, h.relPath);
          }
          continue;
        }
        continue;
      }
      push(name, relPath);
    }
    return out;
  }

  for (const name of rawNames) {
    push(name, [...parentRelPath, name]);
  }
  return out;
}

/** Union of child names at this level (for length checks and legacy callers). */
export function listChildNamesAtPath(
  leftRoot: Category,
  rightRoot: Category,
  path: string[],
): string[] {
  return listCompareTreeChildren(leftRoot, rightRoot, path).map((c) => c.displayName);
}

export function findChildAtPath(
  root: Category,
  path: string[],
  childName: string,
): Category | undefined {
  const parent = path.length === 0 ? root : findCategoryByPath(root, path);
  if (!parent?.children) {
    return undefined;
  }
  const key = childName.trim().toLowerCase();
  return parent.children.find((c) => c.name.trim().toLowerCase() === key);
}

/** Path key for the document root row in compare trees. */
export const COMPARE_ROOT_PATH_KEY = '__root__';

export function relPathToKey(relPath: string[]): string {
  return relPath.length === 0 ? COMPARE_ROOT_PATH_KEY : relPath.join('/');
}

/** Branch path keys under `relPath` (including that node when it has children). */
export function collectCompareSubtreePathKeys(
  leftRoot: Category,
  rightRoot: Category,
  relPath: string[],
): string[] {
  const keys: string[] = [];
  const walk = (path: string[]) => {
    const children = listCompareTreeChildren(leftRoot, rightRoot, path);
    if (children.length === 0) {
      return;
    }
    keys.push(relPathToKey(path));
    for (const child of children) {
      walk(child.relPath);
    }
  };
  walk(relPath);
  return keys;
}

/** Branch path keys for one document tree (expand/collapse on that side only). */
export function collectSideCompareSubtreePathKeys(root: Category, relPath: string[]): string[] {
  const keys: string[] = [];
  const walk = (path: string[]) => {
    const children = listSideCompareTreeChildren(root, path);
    if (children.length === 0) {
      return;
    }
    keys.push(relPathToKey(path));
    for (const child of children) {
      walk(child.relPath);
    }
  };
  walk(relPath);
  return keys;
}

export function nodeIdToPathKey(
  side: 'left' | 'right',
  nodeId: string,
  pathMap: Map<string, string[]>,
  rootName: string,
): string | null {
  const missingPrefix = `${side}-missing-`;
  if (nodeId.startsWith(missingPrefix)) {
    const pathKey = nodeId.slice(missingPrefix.length);
    return pathKey.length === 0 ? COMPARE_ROOT_PATH_KEY : pathKey;
  }
  const prefix = `${side}-`;
  if (!nodeId.startsWith(prefix)) {
    return null;
  }
  const catId = nodeId.slice(prefix.length);
  const fullPath = pathMap.get(catId);
  if (!fullPath) {
    return null;
  }
  if (fullPath.length === 1 && fullPath[0] === rootName) {
    return COMPARE_ROOT_PATH_KEY;
  }
  if (fullPath[0] === rootName) {
    return fullPath.slice(1).join('/');
  }
  return fullPath.join('/');
}

export function pathKeyToCompareNodeId(
  side: 'left' | 'right',
  root: Category,
  pathKey: string,
): string | null {
  if (pathKey === COMPARE_ROOT_PATH_KEY) {
    return compareTreeNodeId(side, root.id);
  }
  const relPath = pathKey.split('/');
  const cat = findCategoryByPath(root, relPath);
  if (cat) {
    if (cat.contentCategory || listSideCompareTreeChildren(root, relPath).length === 0) {
      return null;
    }
    return compareTreeNodeId(side, cat.id);
  }
  return null;
}

export function expandedPathKeysToNodeIds(
  side: 'left' | 'right',
  root: Category,
  pathKeys: string[],
): string[] {
  const ids: string[] = [];
  for (const pathKey of pathKeys) {
    const id = pathKeyToCompareNodeId(side, root, pathKey);
    if (id) {
      ids.push(id);
    }
  }
  return Array.from(new Set(ids));
}
